import { memo, useMemo } from 'react';
import { css } from '@linaria/core';
import _find from 'lodash/find';
import { ResponsiveProps } from '@cbhq/cds-web';
import { IconButton } from '@cbhq/cds-web/buttons';
import { SelectOption } from '@cbhq/cds-web/controls';
import { Dropdown } from '@cbhq/cds-web/dropdown';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Tooltip } from '@cbhq/cds-web/overlays/Tooltip/Tooltip';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@cbhq/cds-web/tables';
import { TextBody, TextHeadline, TextTitle3 } from '@cbhq/cds-web/typography';

import { useAccountCreditsBalances } from ':cloud/hooks/useAccountCreditsBalances';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { SubscriptionState } from ':cloud/hooks/useSubscriptionState';
import { useGetAccount } from ':cloud/queries/BillingQueries/useGetAccount';
import { useGetAccountItems } from ':cloud/queries/BillingQueries/useGetAccountItems';
import { useGetProjects } from ':cloud/queries/Projects/useGetProjects';
import { useGetResources } from ':cloud/queries/ResourceQueries/useGetResources';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { PaymentMethod } from ':cloud/types/service_proto';
import { ICluster } from ':cloud/types/ts_types';
import { getNextBillDate } from ':cloud/utils/billing';
import { currencyFormatter } from ':cloud/utils/formatters/currency';
import { PAYMENT_METHOD_DISPLAY_CARD_HEIGHT } from ':cloud/widgets/billing/utils';
import { CloudErrorBoundary, EmptyTable } from ':cloud/widgets/sharedcomponents';
import { ErrorView } from ':cloud/widgets/sharedcomponents/ErrorView';

// EMPTY table & MISC cell labels
function InvoiceLabel() {
  return (
    <TextBody as="p" color="foregroundMuted">
      Invoice
    </TextBody>
  );
}
type AutoPayLabelProps = { nextBillDate: string };
function AutoPayLabel({ nextBillDate }: AutoPayLabelProps) {
  return (
    <VStack>
      <TextBody as="p">Auto-pay</TextBody>
      <TextBody as="p" color="foregroundMuted">
        Due {nextBillDate}
      </TextBody>
    </VStack>
  );
}

// PRODUCT SUBSCRIPTION STATUS data
type BillingMethodLabelProps = { subscriptionState: SubscriptionState };
function BillingMethodLabel({ subscriptionState }: BillingMethodLabelProps) {
  const { status, title, shortMessageText } = subscriptionState;
  return (
    <VStack>
      <TextBody as="p">{title}</TextBody>
      <TextBody as="p" color={status === 'trial-near-expiry' ? 'negative' : 'foregroundMuted'}>
        {shortMessageText}
      </TextBody>
    </VStack>
  );
}

// PRODUCT row data
type ProductDataProps = {
  onCancel: () => void;
  onReplacePaymentMethod: () => void;
  paymentMethod?: PaymentMethod;
  subscriptionState: SubscriptionState;
};
function ProductData({
  onCancel,
  onReplacePaymentMethod,
  paymentMethod,
  subscriptionState,
}: ProductDataProps) {
  let products;
  const { activeOrg } = useGetUser();
  const orgId = activeOrg?.organizationId;
  const { accountItems } = useGetAccountItems(orgId);
  const { projects } = useGetProjects(orgId);
  if (accountItems.length > 0) {
    products = accountItems;
  }
  if (accountItems.length === 0 && projects.length > 0) {
    products = projects;
  }

  const { accountCreditsRemaining, isAccountCreditsBalanceLow } = useAccountCreditsBalances();

  const { status } = subscriptionState;
  const isSubscriptionInactive = status === 'canceled' || status === 'trial-expired';

  const options = useMemo(() => {
    const dropdownOptions: { text: string; action: () => void }[] = [];
    if (status === 'active') {
      dropdownOptions.push({ text: 'Cancel subscription', action: onCancel });
    }
    if (paymentMethod && !isSubscriptionInactive) {
      dropdownOptions.push({ text: 'Replace payment method', action: onReplacePaymentMethod });
    }
    return dropdownOptions;
  }, [onCancel, onReplacePaymentMethod, status, isSubscriptionInactive, paymentMethod]);

  const dropdownContent = useMemo(
    () =>
      options.map(({ text, action }) => (
        <SelectOption value={text} title={text} onPress={action} />
      )),
    [options],
  );

  return {
    products: products || [],
    dropdownContent,
    isSubscriptionInactive,
    accountCreditsRemaining,
    isAccountCreditsBalanceLow,
  };
}

// PARTICIPATE row data
function ParticipateData() {
  const { resources } = useGetResources((r: ICluster) => r.clusterType === 'participation');
  const resource = useMemo(
    () => _find(resources, { clusterType: 'participation' }) as ICluster,
    [resources],
  );

  const { activeOrg } = useGetUser();
  const account = useGetAccount(activeOrg?.organizationId)?.account;
  const NO_BILLING_SET = 'n/a';
  const billingEmail = account?.billingEmail || NO_BILLING_SET;

  return { activeCluster: resource, billingEmail };
}

// hook used by the parent component to determine rendering Subscriptions table
export function useHasSubscriptionData() {
  const { activeOrg } = useGetUser();
  const orgId = activeOrg?.organizationId;
  const { accountItems } = useGetAccountItems(orgId);
  const { projects } = useGetProjects(orgId);
  const { activeCluster: activeParticipateClusters } = ParticipateData();
  return accountItems.length > 0 || projects.length > 0 || activeParticipateClusters;
}

type FreeCreditsTableCellProps = {
  paymentMethod?: PaymentMethod;
  children: JSX.Element;
};

// TODO(go/j/CCPLT-551): Revert to regular TableCell when integrating free credits API
function FreeCreditsTableCell({ children }: FreeCreditsTableCellProps) {
  // I hate this too, but it's the only way to override CDS's sky-high
  // specificity. This is a temporary solution until free credits comes out.
  const noBottomBorder = css`
    border-bottom-color: transparent !important;
  `;
  return (
    <TableCell alignItems="flex-start" className={noBottomBorder}>
      {children}
    </TableCell>
  );
}

const responsiveConfigHStack: ResponsiveProps = {
  phone: { spacingStart: 0 },
  tablet: { spacingStart: 1 },
  desktop: { spacingStart: 1 },
};

type BillingYourSubscriptionSectionProps = {
  onCancel: () => void;
  onReplacePaymentMethod: () => void;
  subscriptionState: SubscriptionState;
  paymentMethod?: PaymentMethod;
  paymentMethodCell: React.ReactNode;
};

function BillingYourSubscriptionSection({
  onCancel,
  onReplacePaymentMethod,
  subscriptionState,
  paymentMethod,
  paymentMethodCell,
}: BillingYourSubscriptionSectionProps) {
  const {
    products,
    dropdownContent,
    isSubscriptionInactive,
    accountCreditsRemaining,
    isAccountCreditsBalanceLow,
  } = ProductData({
    onCancel,
    paymentMethod,
    onReplacePaymentMethod,
    subscriptionState,
  });
  const { activeCluster, billingEmail } = ParticipateData();

  const hasSubscriptions = products.length > 0 || activeCluster;

  const { isPhone } = useSimpleBreakpoints();

  return (
    <TableBody>
      {!hasSubscriptions ? (
        <EmptyTable>
          <TextHeadline as="p" spacingTop={2} color="foregroundMuted">
            You have no products.
          </TextHeadline>
        </EmptyTable>
      ) : (
        <>
          {/* TODO: refactor this using useGetProducts and mapping products */}
          {/* https://jira.coinbase-corp.com/browse/CCPLT-453 */}
          {products.length > 0 && (
            <>
              <TableRow disableHoverIndicator>
                <FreeCreditsTableCell>
                  <TextHeadline as="p">
                    Wallet as a Service {isSubscriptionInactive && '(inactive)'}
                  </TextHeadline>
                </FreeCreditsTableCell>
                <FreeCreditsTableCell>
                  {subscriptionState.status === 'active' ? (
                    <AutoPayLabel nextBillDate={getNextBillDate()} />
                  ) : (
                    <BillingMethodLabel subscriptionState={subscriptionState} />
                  )}
                </FreeCreditsTableCell>
                <FreeCreditsTableCell>
                  <Box minWidth={isPhone ? 0 : 255}>
                    <HStack gap={2} responsiveConfig={responsiveConfigHStack} alignItems="center">
                      {!isPhone && <Icon color="foreground" size="m" name="giftCard" />}
                      <VStack>
                        <TextHeadline as="h4">Credits</TextHeadline>
                        <HStack alignItems="center" gap={1}>
                          <TextBody
                            as="p"
                            color={isAccountCreditsBalanceLow ? 'negative' : 'foregroundMuted'}
                          >
                            {currencyFormatter(accountCreditsRemaining)} remaining
                          </TextBody>
                          <Tooltip
                            placement="bottom"
                            content="This balance will update when you receive your next invoice."
                          >
                            <Icon name="questionMark" size="s" color="foregroundMuted" />
                          </Tooltip>
                        </HStack>
                      </VStack>
                    </HStack>
                  </Box>
                </FreeCreditsTableCell>
              </TableRow>
              <TableRow disableHoverIndicator>
                <TableCell>&nbsp;</TableCell>
                <TableCell>&nbsp;</TableCell>
                <TableCell
                  end={
                    dropdownContent[0] && (
                      <Dropdown content={dropdownContent}>
                        <IconButton transparent name="more" />
                      </Dropdown>
                    )
                  }
                >
                  <Box spacingBottom={2}>{paymentMethodCell}</Box>
                </TableCell>
              </TableRow>
            </>
          )}
          {activeCluster && (
            <TableRow disableHoverIndicator>
              <TableCell>
                <TextHeadline as="p">Participate</TextHeadline>
              </TableCell>
              <TableCell>
                <InvoiceLabel />
              </TableCell>
              <TableCell width="40%">
                <HStack
                  responsiveConfig={responsiveConfigHStack}
                  gap={2}
                  minHeight={PAYMENT_METHOD_DISPLAY_CARD_HEIGHT}
                  alignItems="center"
                >
                  {!isPhone && <Icon color="foreground" size="m" name="email" />}
                  <TextBody as="p" color="foregroundMuted" overflow="break">
                    {billingEmail}
                  </TextBody>
                </HStack>
              </TableCell>
            </TableRow>
          )}
        </>
      )}
    </TableBody>
  );
}

function BillingYourSubscriptionSectionWrapper(props: BillingYourSubscriptionSectionProps) {
  const { isPhone } = useSimpleBreakpoints();
  return (
    <VStack gap={3}>
      <TextTitle3 as="h3">Your subscription</TextTitle3>
      <Table bordered variant="ruled" compact={isPhone}>
        <TableHeader>
          <TableRow>
            <TableCell title="Product" />
            <TableCell title="Billing" />
            <TableCell title="Payment method" alignItems={isPhone ? 'flex-start' : undefined} />
          </TableRow>
        </TableHeader>
        <CloudErrorBoundary
          name="BillingYourSubscriptionSection"
          fallback={
            <TableBody>
              <EmptyTable>
                <ErrorView />
              </EmptyTable>
            </TableBody>
          }
        >
          <BillingYourSubscriptionSection {...props} />
        </CloudErrorBoundary>
      </Table>
    </VStack>
  );
}

export default memo(BillingYourSubscriptionSectionWrapper);
